/*
 * Copyright (c) 2023, WSO2 LLC. (http://www.wso2.com). All Rights Reserved.
 *
 * This software is the property of WSO2 LLC. and its suppliers, if any.
 * Dissemination of any information or reproduction of any material contained
 * herein is strictly forbidden, unless permitted by WSO2 in accordance with
 * the WSO2 Commercial License available at http://wso2.com/licenses.
 * For specific language governing the permissions and limitations under
 * this license, please see the license as well as any agreement you’ve
 * entered into with WSO2 governing the purchase of this software and any
 * associated services.
 */

import { RawUser } from 'types';
import {
  getRecentOrganization,
  setRecentOrganization,
  setRecentProject,
} from 'data/recentNavigation';

/**
 * Represent an user logged in to the application, There will be always one user per session and
 * this user details will be persisted in browser local-storage.
 */
export default class User {
  public static STORAGE_PREFIX = 'choreo-user';

  name?: string;

  remember?: boolean;

  _organization?: string;

  _scopes?: string[];

  /**
   * Map of users (key = Organization, value = User instance)
   * @type {Map}
   * @private
   */
  private static _userMap = new Map();

  /**
   * Create a user for the given organization
   * @param {string} name - name of the user
   * @param {string} organization - name of the organization
   * @param {boolean} remember
   * @returns {User|null} user object
   */
  constructor(organization: string, name?: string, remember = false) {
    /* eslint-disable no-underscore-dangle */
    // indicate “private” members of User singleton
    const user = User._userMap.get(organization);
    if (user) {
      return user;
    }
    this.name = name;
    this.scopes = [];
    this.remember = remember;
    this._organization = organization;
    User._userMap.set(organization, this);
  }

  /**
   * OAuth scopes which are available for use by this user
   * @returns {Array} - An array of scopes
   */
  get scopes() {
    return this._scopes || [];
  }

  /**
   * Set OAuth scopes available to be used by this user
   * @param {Array} newScopes - An array of scopes
   */
  set scopes(newScopes: string[]) {
    this._scopes = newScopes;
  }

  /**
   * User utility method to create an user from JSON object.
   * @param {Object} userJson - Need to provide user information in JSON structure to create an user object
   * @param {String} organization - Name of the organization to be assigned to the user
   * @returns {User} - An instance of User(this) class.
   */
  public static fromJson(userJson: RawUser) {
    if (!userJson.organization) {
      throw new Error(
        'Need to provide Organization in the JSON object, to create an user'
      );
    }
    const { name, organization, scopes, remember } = userJson;
    const newUser = new User(organization, name);
    if (scopes) {
      newUser.scopes = scopes;
    }
    if (remember) {
      newUser.remember = remember;
    }
    return newUser;
  }

  public static init(userJson: RawUser) {
    const newUser = User.fromJson(userJson);
    if (newUser) {
      localStorage.setItem(
        User.STORAGE_PREFIX,
        JSON.stringify(newUser.toJson())
      );
      const recentOrganization = getRecentOrganization();
      if (newUser.organization !== recentOrganization) {
        setRecentOrganization(newUser.organization);
        setRecentProject('');
      }
    }
    return newUser;
  }

  /**
   * Provide user data in JSON structure.
   * @returns {JSON} - JSON representation of the user object
   */
  toJson() {
    return {
      name: this.name,
      organization: this._organization,
      scopes: this.scopes,
      remember: this.remember,
    };
  }

  /**
   * Check whether the current user has admin role or not
   */
  isAdmin() {
    return this.scopes.includes('apim:admin');
  }

  /**
   * Check whether the current user has creator role or not
   */
  isCreator() {
    return this.scopes.includes('apim:api_create');
  }

  get organization() {
    if (this._organization) {
      return this._organization;
    }
    return '';
  }

  set organization(organizationHandler: string) {
    const userString = localStorage.getItem(User.STORAGE_PREFIX);
    if (userString) {
      const userProps = JSON.parse(userString);
      userProps.organization = organizationHandler;
      localStorage.setItem(User.STORAGE_PREFIX, JSON.stringify(userProps));
      this._organization = organizationHandler;
    }
  }

  public static getUser() {
    const rawUser = localStorage.getItem(User.STORAGE_PREFIX);
    if (rawUser) {
      return User.fromJson(JSON.parse(rawUser));
    }
    return null;
  }

  public static remove() {
    localStorage.removeItem(User.STORAGE_PREFIX);
    User._userMap = new Map();
  }
}
